/**
 * BlogService - Manages blog post loading, caching, and metadata
 * Provides a centralized service for all blog-related operations
 */

import { getAllPosts, getPostBySlug, getFeaturedPosts, getPostsByTag } from '../blog/index.js';

class BlogService {
  constructor() {
    this.postsCache = new Map();
    this.metadataCache = null;
    this.lastCacheUpdate = null;
    this.CACHE_DURATION = 5 * 60 * 1000; // 5 minutes in milliseconds
  }

  /**
   * Get all blog posts metadata, sorted by date (newest first)
   */
  async getAllPosts() {
    return await getAllPosts();
  }

  /**
   * Get a specific post's metadata by slug
   */
  async getPostMetadata(slug) {
    return await getPostBySlug(slug);
  }

  /**
   * Get featured posts
   */
  async getFeaturedPosts() {
    return await getFeaturedPosts();
  }

  /**
   * Get posts by tag
   */
  async getPostsByTag(tag) {
    return await getPostsByTag(tag);
  }

  /**
   * Load the actual content of a blog post
   */
  async loadPostContent(slug) {
    // Check if we have cached content
    if (this.postsCache.has(slug)) {
      const cached = this.postsCache.get(slug);
      if (Date.now() - cached.timestamp < this.CACHE_DURATION) {
        return cached.content;
      }
    }

    // Get post metadata
    const postMetadata = await this.getPostMetadata(slug);
    console.log('📋 BlogService: Full metadata object:', postMetadata);
    console.log('📋 BlogService: Filename property:', postMetadata?.filename);
    if (!postMetadata) {
      throw new Error(`Blog post with slug '${slug}' not found`);
    }

    try {
      // Fetch the markdown file as text
      console.log('🔍 BlogService: Fetching markdown from:', `/blog/posts/${postMetadata.filename}`);
      const response = await fetch(`/blog/posts/${postMetadata.filename}`);
      console.log('📡 BlogService: Response status:', response.status, response.statusText);
      console.log('📡 BlogService: Response headers:', Object.fromEntries(response.headers.entries()));
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      const content = await response.text();
      console.log('📝 BlogService: Raw content preview:', content.substring(0, 200));

      // Cache the content
      this.postsCache.set(slug, {
        content,
        timestamp: Date.now()
      });

      return content;
    } catch (error) {
      console.error(`Failed to load blog post content for slug: ${slug}`, error);
      throw new Error(`Failed to load blog post: ${postMetadata.title}`);
    }
  }

  /**
   * Get navigation data (previous/next posts) for a given slug
   */
  async getPostNavigation(currentSlug) {
    const allPosts = await this.getAllPosts();
    const currentIndex = allPosts.findIndex(post => post.slug === currentSlug);
    
    if (currentIndex === -1) {
      return { previous: null, next: null };
    }

    return {
      previous: currentIndex > 0 ? allPosts[currentIndex - 1] : null,
      next: currentIndex < allPosts.length - 1 ? allPosts[currentIndex + 1] : null
    };
  }

  /**
   * Search posts by title or content (basic implementation)
   */
  async searchPosts(query) {
    if (!query || query.trim().length === 0) {
      return await this.getAllPosts();
    }

    const searchTerm = query.toLowerCase();
    const allPosts = await this.getAllPosts();
    return allPosts.filter(post => 
      post.title.toLowerCase().includes(searchTerm) ||
      post.description.toLowerCase().includes(searchTerm) ||
      (post.tags && post.tags.some(tag => tag.toLowerCase().includes(searchTerm)))
    );
  }

  /**
   * Get all unique tags from all posts
   */
  async getAllTags() {
    const allPosts = await this.getAllPosts();
    const tagSet = new Set();
    
    allPosts.forEach(post => {
      if (post.tags) {
        post.tags.forEach(tag => tagSet.add(tag));
      }
    });

    return Array.from(tagSet).sort();
  }

  /**
   * Generate a summary/excerpt from post content
   */
  generateExcerpt(content, maxLength = 200) {
    if (!content) return '';
    
    // Remove markdown formatting for excerpt
    const plainText = content
      .replace(/#{1,6}\s+/g, '') // Remove headers
      .replace(/\*\*(.*?)\*\*/g, '$1') // Remove bold
      .replace(/\*(.*?)\*/g, '$1') // Remove italic
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // Remove links, keep text
      .replace(/```[\s\S]*?```/g, '') // Remove code blocks
      .replace(/`([^`]+)`/g, '$1') // Remove inline code
      .replace(/^\s*[-*+]\s+/gm, '') // Remove list markers
      .replace(/\n+/g, ' ') // Replace newlines with spaces
      .trim();

    if (plainText.length <= maxLength) {
      return plainText;
    }

    // Find the last complete word within the limit
    const truncated = plainText.substring(0, maxLength);
    const lastSpaceIndex = truncated.lastIndexOf(' ');
    
    if (lastSpaceIndex > maxLength * 0.8) {
      return truncated.substring(0, lastSpaceIndex) + '...';
    }
    
    return truncated + '...';
  }

  /**
   * Format date for display
   */
  formatDate(dateString, format = 'long') {
    const date = new Date(dateString);
    
    if (format === 'short') {
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    }
    
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  }

  /**
   * Calculate reading time estimate (words per minute)
   */
  calculateReadingTime(content, wordsPerMinute = 200) {
    if (!content) return 0;
    
    const wordCount = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(wordCount / wordsPerMinute);
    
    return minutes;
  }

  /**
   * Get blog statistics
   */
  async getBlogStats() {
    const allPosts = await this.getAllPosts();
    const allTags = await this.getAllTags();
    const featuredPosts = await this.getFeaturedPosts();
    
    return {
      totalPosts: allPosts.length,
      totalTags: allTags.length,
      featuredPosts: featuredPosts.length,
      oldestPost: allPosts.length > 0 ? allPosts[allPosts.length - 1] : null,
      newestPost: allPosts.length > 0 ? allPosts[0] : null
    };
  }

  /**
   * Clear the cache (useful for development or manual refresh)
   */
  clearCache() {
    this.postsCache.clear();
    this.metadataCache = null;
    this.lastCacheUpdate = null;
  }
}

// Export a singleton instance
const blogService = new BlogService();
export default blogService;